/**
 * gift-tote.js — gift-with-purchase auto-add for the ENLIVENLIFT cart.
 *
 * Shopify's automatic Buy X Get Y discount only zeroes the price of a gift line
 * that is already in the cart; it never creates one. This element watches the
 * server-computed state rendered by `snippets/gift-tote.liquid` and keeps the
 * gift line in sync with the cart:
 *
 *   qualifies && gift absent  -> add the gift
 *   !qualifies && gift present -> remove the gift
 *
 * All eligibility logic lives in Liquid (one source of truth, re-evaluated by
 * Shopify on every cart render). This file only decides *when to act* on it.
 *
 * Safety properties:
 *   - Exactly one instance may mutate the cart. The cart page renders both the
 *     page footer and the drawer, and an AJAX re-render can refresh one but not
 *     the other; without an elected controller the stale twin acts on stale
 *     state. Non-controllers stay display-only.
 *   - Single in-flight mutation at a time (module-scoped guard) — the element is
 *     destroyed and recreated on every section re-render, so per-instance state
 *     would not survive.
 *   - A shopper who removes the gift by hand is not fought with; the offer is
 *     re-armed only once the cart drops back below the threshold.
 *   - If Shopify declines to discount the gift (threshold semantics differing
 *     from our prediction), the gift is pulled back out and not re-added at that
 *     cart value — the shopper is never charged for a "free" item.
 *   - Gift stock exhaustion degrades silently; checkout is never blocked.
 */
(function () {
  'use strict';

  const DECLINED_KEY = 'gwp:tote:declined';

  /** Only one cart mutation from this module may be in flight at a time. */
  let inFlight = false;
  /**
   * Gift presence as of the last state this module observed or caused.
   * Used to tell "the shopper removed the gift" apart from "the gift was never
   * there". It must be updated by our own mutations too, not just by renders:
   * an add does not always trigger a re-render of the surface that hosts this
   * element, so relying on renders alone leaves it stale and the shopper's
   * removal gets undone.
   */
  let lastGiftInCart = null;
  /** Qualifying subtotal at which Shopify refused to discount the gift. */
  let pricingRejectedAt = null;

  const readDeclined = () => {
    try {
      return sessionStorage.getItem(DECLINED_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const writeDeclined = (value) => {
    try {
      if (value) {
        sessionStorage.setItem(DECLINED_KEY, '1');
      } else {
        sessionStorage.removeItem(DECLINED_KEY);
      }
    } catch (error) {
      /* Private browsing — degrade to "never declined". */
    }
  };

  /**
   * Elect the single instance allowed to mutate the cart. The cart page's own
   * footer wins over the drawer, because that is the surface the shopper is
   * looking at and the one Shopify re-renders on quantity changes.
   */
  const isController = (element) => {
    const instances = Array.from(document.querySelectorAll('gift-tote'));
    const pageInstance = instances.find((node) => node.dataset.surface === 'page');
    return (pageInstance || instances[0]) === element;
  };

  const announceCartUpdate = (cartData) => {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-tote', cartData: cartData });
  };

  const requestConfig = () => {
    if (typeof fetchConfig === 'function') return fetchConfig('javascript');
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
    };
  };

  class GiftTote extends HTMLElement {
    connectedCallback() {
      const addButton = this.querySelector('[data-gift-add]');
      if (addButton) {
        addButton.addEventListener('click', () => {
          writeDeclined(false);
          pricingRejectedAt = null;
          this.addGift();
        });
      }
      this.evaluate();
    }

    get enabled() {
      return this.dataset.enabled === 'true';
    }
    get qualifies() {
      return this.dataset.qualifies === 'true';
    }
    get giftInCart() {
      return this.dataset.giftInCart === 'true';
    }
    /** True when the gift is in the cart but Shopify has NOT zeroed its price. */
    get giftPriced() {
      return this.dataset.giftPriced === 'true';
    }
    get available() {
      return this.dataset.available === 'true';
    }
    get variantId() {
      return Number(this.dataset.variantId);
    }
    get qualifyingTotal() {
      return Number(this.dataset.qualifyingTotal);
    }

    evaluate() {
      if (!this.enabled || !this.variantId) return;
      // Display-only twin: render the message, never touch the cart.
      if (!isController(this)) return;

      // A gift that is present but still priced means our threshold prediction
      // disagreed with Shopify. Withdraw it and stop offering at this value.
      if (this.giftInCart && this.giftPriced) {
        pricingRejectedAt = this.qualifyingTotal;
        this.hidden = true;
        this.removeGift();
        return;
      }

      // The shopper removed the gift themselves: it was here last render, the
      // cart still qualifies, and it is gone now. Respect that choice.
      if (lastGiftInCart === true && !this.giftInCart && this.qualifies) {
        writeDeclined(true);
      }
      // Falling back below the threshold re-arms the offer for next time.
      if (!this.qualifies) {
        writeDeclined(false);
        pricingRejectedAt = null;
      }
      lastGiftInCart = this.giftInCart;

      if (readDeclined()) {
        this.setAttribute('data-declined', '');
      } else {
        this.removeAttribute('data-declined');
      }

      if (!this.qualifies && this.giftInCart) {
        this.removeGift();
        return;
      }

      if (
        this.qualifies &&
        !this.giftInCart &&
        this.available &&
        !readDeclined() &&
        pricingRejectedAt !== this.qualifyingTotal
      ) {
        this.addGift();
      }
    }

    addGift() {
      if (inFlight) return;
      inFlight = true;

      fetch(routes.cart_add_url, {
        ...requestConfig(),
        body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1 }] }),
      })
        .then((response) => response.json())
        .then((data) => {
          // 422 = out of stock or otherwise unpurchasable. Say nothing, break nothing.
          if (data && data.status) {
            this.dataset.available = 'false';
            return;
          }
          lastGiftInCart = true;
          announceCartUpdate(data);
        })
        .catch(() => {
          /* Network failure — the next cart render retries. */
        })
        .finally(() => {
          inFlight = false;
        });
    }

    removeGift() {
      if (inFlight) return;
      inFlight = true;

      fetch(routes.cart_change_url, {
        ...requestConfig(),
        body: JSON.stringify({ id: String(this.variantId), quantity: 0 }),
      })
        .then((response) => response.json())
        .then((data) => {
          lastGiftInCart = false;
          announceCartUpdate(data);
        })
        .catch(() => {})
        .finally(() => {
          inFlight = false;
        });
    }
  }

  if (!customElements.get('gift-tote')) {
    customElements.define('gift-tote', GiftTote);
  }
})();
